import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Pencil } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";

import { APIError } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { useUpdateObra } from "../../hooks/use-update-obra.ts";
import type { Obra } from "../../obra.ts";
import { obraSchema } from "../../schemas/obra-schema.ts";

const nameSchema = obraSchema.pick({ name: true });

type NameFormValues = z.infer<typeof nameSchema>;

export function ObraNameRow({ obra }: { obra: Obra }) {
	const { has } = usePermissions();
	const canUpdate = has("constructions:update");
	const updateObra = useUpdateObra(obra.id);
	const [editing, setEditing] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);

	const {
		register,
		handleSubmit,
		reset,
		setError,
		formState: { errors },
	} = useForm<NameFormValues>({
		resolver: zodResolver(nameSchema),
		defaultValues: { name: obra.name },
	});

	function startEditing() {
		reset({ name: obra.name });
		setFormError(null);
		setEditing(true);
	}

	function cancel() {
		reset({ name: obra.name });
		setFormError(null);
		setEditing(false);
	}

	function applyAPIError(err: unknown, fallback: string) {
		if (err instanceof APIError) {
			const violation = err.violations.name;
			if (violation) {
				setError("name", { message: violation });
				return;
			}
			setFormError(err.message || fallback);
			return;
		}
		setFormError(err instanceof Error ? err.message : fallback);
	}

	async function onSubmit(values: NameFormValues) {
		setFormError(null);

		if (values.name === obra.name) {
			setEditing(false);
			return;
		}

		try {
			await updateObra.mutateAsync({ name: values.name });
			toast.success("Nome da obra atualizado com sucesso!");
			setEditing(false);
		} catch (err) {
			applyAPIError(err, "Não foi possível atualizar o nome da obra.");
		}
	}

	const isPending = updateObra.isPending;

	if (!editing) {
		return (
			<div className="obra-settings-row">
				<div className="obra-settings-row__body">
					<p className="obra-settings-row__label">Nome da obra</p>
					<p className="obra-settings-row__value">{obra.name}</p>
				</div>
				{canUpdate && (
					<Button variant="outline" size="sm" onClick={startEditing}>
						<Pencil />
						Editar
					</Button>
				)}
			</div>
		);
	}

	return (
		<div className="obra-settings-row">
			<div className="obra-settings-row__body">
				<Label htmlFor="obra-name" className="obra-settings-row__label">
					Nome da obra
				</Label>
			</div>
			<form
				className="obra-settings-row__form"
				onSubmit={handleSubmit(onSubmit)}
				noValidate
			>
				<div className="obra-settings-row__field">
					<Input
						id="obra-name"
						placeholder="Ex.: Bloco A"
						aria-invalid={!!errors.name}
						{...register("name")}
					/>
					{errors.name && <p className="form-error">{errors.name.message}</p>}
					{formError && (
						<p role="alert" className="form-error-alert">
							{formError}
						</p>
					)}
				</div>
				<div className="obra-settings-row__actions">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={cancel}
						disabled={isPending}
					>
						Cancelar
					</Button>
					<Button type="submit" size="sm" disabled={isPending}>
						{isPending && <Loader2 className="obra-settings-row__spinner" />}
						Salvar
					</Button>
				</div>
			</form>
		</div>
	);
}
